import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IconX } from './Icon';

type Alert = { id: number; title: string; message: string; type: string; created_at: string };

const API_V1 = (import.meta.env.VITE_API_BASE_V1 as string) || 'https://vnav.lk/api/vpnee';

export default function NoticesBanner() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('vpnee_dismissed_alerts') || '[]')); } catch { return new Set(); }
  });

  const fetchAlerts = async () => {
    try {
      const r = await fetch(`${API_V1}/user/system_alerts`, { headers: { 'Accept': 'application/json' } });
      const j = await r.json();
      const list: Alert[] = j.alerts || j.data?.alerts || [];
      // Filter active and not dismissed
      const active = list.filter(a => !dismissed.has(a.id));
      setAlerts(active);
    } catch {}
  };

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 60000);
    const onVisible = () => { if (!document.hidden) fetchAlerts(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const dismiss = (id: number) => {
    const next = new Set(dismissed); next.add(id);
    setDismissed(next);
    localStorage.setItem('vpnee_dismissed_alerts', JSON.stringify([...next]));
    setAlerts(a => a.filter(x => x.id !== id));
    // Tell backend who dismissed which notice — admin can identify and get conclusion
    try {
      let username: string | null = null;
      try { const u = JSON.parse(localStorage.getItem('vpnee_user') || 'null'); username = u?.username || null; } catch {}
      const token = localStorage.getItem('vpnee_token');
      const headers: Record<string,string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = 'Bearer ' + token;
      fetch(`${API_V1}/user/alerts/read`, { method: 'POST', headers, body: JSON.stringify({ alert_id: id, username }) }).catch(()=>{});
    } catch {}
  };

  if (alerts.length === 0) return null;

  const typeColor = (t: string) => {
    switch (t) {
      case 'warning': return 'border-amber-400/30 bg-amber-500/10 text-amber-100';
      case 'danger':
      case 'critical': return 'border-red-500/30 bg-red-500/10 text-red-100';
      case 'success': return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100';
      default: return 'border-[color:var(--color-brand)]/30 bg-[color:var(--color-brand)]/10 text-white';
    }
  };

  return (
    <div className="relative z-30 max-w-6xl mx-auto px-4 sm:px-6 pt-[72px] pb-2 space-y-2 pointer-events-none">
      <AnimatePresence>
        {alerts.map(a => (
          <motion.div key={a.id} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className={`pointer-events-auto rounded-2xl border p-4 flex items-start gap-3 overflow-hidden ${typeColor(a.type)} backdrop-blur-md shadow-lg`}>
            <span className="w-2 h-2 rounded-full bg-current mt-1.5 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="font-bold text-sm leading-tight break-words [overflow-wrap:anywhere]">{a.title}</div>
              <div className="text-sm opacity-90 mt-0.5 leading-relaxed break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{a.message}</div>
              <div className="text-xs opacity-60 mt-1">Published {new Date(a.created_at).toLocaleDateString()} · ID #{a.id}</div>
            </div>
            <button onClick={() => dismiss(a.id)} className="shrink-0 w-8 h-8 rounded-xl grid place-items-center bg-black/20 hover:bg-black/30 border border-white/10 text-current" aria-label="Dismiss"><IconX width={14} height={14} /></button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
